import React, { useState, useRef, useCallback } from "react";
import { decodeAndSanitizeHtml } from "./textAnalysisUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faPhone,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faQuestionCircle,
  faShieldAlt,
  faTimes,
  faPaperPlane,
  faChartBar,
  faEdit,
  faPlus,
  faComments,
  faTrash,
  faUser,
  faUserShield,
  faCommentDots,
  faShareAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import { formatDateTime } from "../../utils/DateTimeUtil";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import { useSubscription } from "../../context/SubscriptionContext";
import EmptyStatePanel from "../common/EmptyStatePanel";
import HelpGuideButton from "../../HelpGuideButton";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/TextConversationMobile.css";
import "./css/SmsAnalysisSimpleMobile.css";
import "../../css/Common.css";

// ─────────────────────────────────────────────
// Accordion Section
// ─────────────────────────────────────────────
const AccordionSection = ({
  title,
  icon,
  children,
  defaultOpen = false,
  badge,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`tcam-accordion ${open ? "tcam-accordion--open" : ""}`}>
      <button
        type="button"
        className="tcam-accordion-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="tcam-accordion-title">
          {icon && (
            <FontAwesomeIcon icon={icon} className="tcam-accordion-icon" />
          )}
          {title}
          {badge && <span className="tcam-accordion-badge">{badge}</span>}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
      </button>
      {open && <div className="tcam-accordion-body">{children}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────
// Risk helpers
// ─────────────────────────────────────────────
const getRiskLevelIcon = (riskLevel) => {
  switch (riskLevel?.toLowerCase()) {
    case "high":
    case "danger":
      return faTimesCircle;
    case "medium":
    case "warning":
      return faExclamationTriangle;
    case "low":
    case "safe":
      return faCheckCircle;
    default:
      return faQuestionCircle;
  }
};

const getRiskClass = (riskLevel) => {
  switch (riskLevel?.toLowerCase()) {
    case "high":
    case "danger":
      return "high";
    case "medium":
    case "warning":
      return "medium";
    case "low":
    case "safe":
      return "low";
    default:
      return "unknown";
  }
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const TextConversationMobile = ({
  phoneNumber,
  messages,
  analysisResults,
  error,
  isLoading,
  hasUserInput,
  phoneValidation,
  isPhoneDisabled,
  handlePhoneChange,
  handleMessageTextChange,
  handleMessageSenderChange,
  addMessage,
  removeMessage,
  handleClearAnalysis,
  handleSubmit,
  isFormValid,
  shouldShowPhoneValidationError,
  getOverallRiskLevel,
  getOverallRiskType,
  getOverallRecommendation,
  reportId,
  phoneInputKey,
}) => {
  const [activeSlide, setActiveSlide] = useState("form");
  const [isDatabaseExpanded, setIsDatabaseExpanded] = useState(false);
  const share = useShareAlert();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const containerRef = useRef(null);
  const { t } = useTranslation();

  const scrollTop = useCallback(() => {
    // Scroll the nearest scrollable ancestor (e.g. .hero on mobile) to top
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const showResults = useCallback(() => {
    setActiveSlide("results");
    scrollTop();
  }, [scrollTop]);

  const showForm = useCallback(() => {
    setActiveSlide("form");
    scrollTop();
  }, [scrollTop]);

  // Auto-slide to results when analysis completes
  const prevResultsRef = useRef(analysisResults);
  if (analysisResults && analysisResults !== prevResultsRef.current) {
    prevResultsRef.current = analysisResults;
    if (activeSlide === "form") {
      setTimeout(() => setActiveSlide("results"), 100);
    }
  }

  const overallRisk = getOverallRiskLevel();
  const riskClass = getRiskClass(overallRisk);

  // Render a single message bubble input
  const renderMessageBubble = (message, index) => {
    const isFromThem = message.sender === "them";
    return (
      <div
        key={message.id}
        className={`tcam-bubble-wrapper ${isFromThem ? "tcam-bubble-left" : "tcam-bubble-right"}`}
      >
        <div className="tcam-sender-group">
          <button
            type="button"
            className={`tcam-sender-btn ${isFromThem ? "tcam-sender-active" : ""}`}
            onClick={() => handleMessageSenderChange(message.id, "them")}
            disabled={isLoading}
            title={t("textConv.form.fromThem")}
          >
            <FontAwesomeIcon icon={faUser} />
          </button>
          <span className="tcam-sender-label">{t("textConv.form.them")}</span>
        </div>

        <div
          className={`tcam-bubble-col ${isFromThem ? "tcam-col-left" : "tcam-col-right"}`}
        >
          <div className="tcam-bubble-controls">
            <span className="tcam-msg-number">#{index + 1}</span>
            {messages.length > 1 && (
              <button
                type="button"
                onClick={() => removeMessage(message.id)}
                disabled={isLoading}
                className="tcam-remove-btn"
                title={t("textConv.form.remove")}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
          <div
            className={`tcam-bubble ${isFromThem ? "tcam-bubble-them" : "tcam-bubble-you"}`}
          >
            <textarea
              value={message.text}
              onChange={(e) =>
                handleMessageTextChange(message.id, e.target.value)
              }
              placeholder={
                isFromThem
                  ? t("textConv.form.theirMessage")
                  : t("textConv.form.yourMessage")
              }
              rows={2}
              disabled={isLoading}
              className="tcam-bubble-input"
              maxLength={1000}
            />
          </div>
        </div>

        <div className="tcam-sender-group">
          <button
            type="button"
            className={`tcam-sender-btn tcam-sender-you ${!isFromThem ? "tcam-sender-active" : ""}`}
            onClick={() => handleMessageSenderChange(message.id, "you")}
            disabled={isLoading}
            title={t("textConv.form.fromYou")}
          >
            <FontAwesomeIcon icon={faUserShield} />
          </button>
          <span className="tcam-sender-label">{t("textConv.form.you")}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="tcam-container" ref={containerRef}>
      <div
        className={`tcam-view-slider ${
          activeSlide === "results" ? "tcam-slide-results" : ""
        }`}
      >
        {/* ── SLIDE 1: Form Input ── */}
        <div className="tcam-slide tcam-slide-form">
          <div className="tcam-form-wrapper">
            {/* Page Header */}
            <div className="sasm-header">
              <div className="sasm-header-top">
                <h1 className="main-title">{t("textConv.page.title")}</h1>
                <HelpGuideButton guideId={3} />
              </div>
              <p className="common-subtitle">{t("textConv.mobile.subtitle")}</p>
            </div>

            {/* Section Header */}
            <div className="tcam-section-header">
              <FontAwesomeIcon
                icon={faComments}
                className="tcam-section-icon"
              />
              <span>{t("textConv.form.conversationInputs")}</span>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="tcam-error-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="tcam-form">
              {/* Phone Number */}
              <div className="tcam-form-field">
                <div
                  className={`tcam-field-label ${isPhoneDisabled ? "tcam-disabled" : ""}`}
                >
                  <FontAwesomeIcon icon={faPhone} className="tcam-field-icon" />
                  <label>{t("textAnalysis.form.phoneLabel")}</label>
                </div>
                <div className="tcam-phone-input-wrap">
                  <InternationalPhoneInput
                    key={phoneInputKey}
                    id="tcam-phone-number"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="Enter phone number"
                    disabled={isPhoneDisabled}
                    className="phone-input compact"
                  />
                </div>
                {shouldShowPhoneValidationError() && (
                  <div className="tcam-validation-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {t("textAnalysis.form.invalidPhone")}
                  </div>
                )}
              </div>

              {/* Messages Section */}
              <div className="tcam-form-field">
                <div className="tcam-field-label">
                  <FontAwesomeIcon
                    icon={faCommentDots}
                    className="tcam-field-icon"
                  />
                  <label>{t("textConv.form.messagesTapLabel")}</label>
                </div>

                <div className="tcam-messages-list">
                  {messages.map((message, index) =>
                    renderMessageBubble(message, index),
                  )}
                </div>

                <button
                  type="button"
                  onClick={addMessage}
                  disabled={isLoading}
                  className="tcam-add-message-btn"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {t("textConv.form.addMessage")}
                </button>
              </div>

              {/* Actions */}
              <div className="btn-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || !isFormValid()}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isLoading
                    ? t("textAnalysis.form.analyzing")
                    : t("textConv.form.analyzeBtn")}
                </button>

                {hasUserInput && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleClearAnalysis}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    {t("textConv.form.clearAll")}
                  </button>
                )}
              </div>
            </form>

            {/* Quick-jump to results */}
            {analysisResults && (
              <button
                type="button"
                className="btn btn-secondary tcam-view-results-link"
                onClick={showResults}
              >
                <FontAwesomeIcon icon={faChartBar} />
                {t("textAnalysis.mobile.viewResults")}
              </button>
            )}
          </div>
        </div>

        {/* ── SLIDE 2: Analysis Results ── */}
        <div className="tcam-slide tcam-slide-detail">
          <div className="tcam-results-wrapper">
            {/* Back button */}
            <button type="button" className="tcam-back-btn" onClick={showForm}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t("textAnalysis.mobile.backToForm")}
            </button>

            {analysisResults ? (
              <>
                {/* ── Threat Banner (desktop style) ── */}
                <div className="case-details">
                  <div
                    className={`threat-banner tcam-threat-banner ${riskClass === "high" || riskClass === "medium" ? "threat-detected" : "safe"}`}
                  >
                    <div className="threat-icon">
                      <FontAwesomeIcon
                        icon={
                          riskClass === "high" || riskClass === "medium"
                            ? faExclamationTriangle
                            : faCheckCircle
                        }
                      />
                    </div>
                    <h2 className="threat-title">
                      {riskClass === "high" || riskClass === "medium"
                        ? `${overallRisk || ""} ${t("textAnalysis.mobile.riskDetectedSuffix")}`.trim()
                        : t("textAnalysis.mobile.noThreats")}
                    </h2>
                    {(riskClass === "high" || riskClass === "medium") && (
                      <button
                        className="threat-banner-share-btn"
                        onClick={
                          isIndividualPlan
                            ? undefined
                            : (e) => {
                                e.stopPropagation();
                                share.toggleShare();
                              }
                        }
                        disabled={isIndividualPlan}
                        title={
                          isIndividualPlan
                            ? t("textAnalysis.mobile.shareIndividual")
                            : t("textAnalysis.mobile.shareFamily")
                        }
                      >
                        <FontAwesomeIcon
                          icon={faShareAlt}
                          className="threat-share-icon"
                        />
                        <span className="threat-share-label">
                          {t("textAnalysis.mobile.share")}
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline Share Form */}
                {share.shareOpen &&
                  (riskClass === "high" || riskClass === "medium") && (
                    <InlineShareForm
                      shareSuccess={share.shareSuccess}
                      shareError={share.shareError}
                      shareMessage={share.shareMessage}
                      setShareMessage={share.setShareMessage}
                      shareLoading={share.shareLoading}
                      onCancel={share.closeShare}
                      onSend={() => share.sendShare(reportId)}
                    />
                  )}

                {/* ── What Should You Do? (desktop style) ── */}
                {getOverallRecommendation() && (
                  <div className="suggestions-section">
                    <h4 className="subsection-title">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="fa-icon-margin-right-8"
                      />
                      {t("textAnalysis.results.whatToDo")}
                    </h4>
                    <div className="tcam-recommendation-text">
                      {getOverallRecommendation()}
                    </div>
                  </div>
                )}

                {/* ── Phone Number Analysis ── */}
                {analysisResults.phoneAnalysis && (
                  <AccordionSection
                    title={t("textAnalysis.phone.title")}
                    icon={faPhone}
                    defaultOpen={true}
                  >
                    <div className="tcam-detail-grid">
                      {analysisResults.phoneAnalysis.riskLevel && (
                        <div className="tcam-detail-row">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.phone.riskLevel")}
                          </span>
                          <span
                            className={`tcam-risk-chip tcam-chip-${getRiskClass(
                              analysisResults.phoneAnalysis.riskLevel,
                            )}`}
                          >
                            <FontAwesomeIcon
                              icon={getRiskLevelIcon(
                                analysisResults.phoneAnalysis.riskLevel,
                              )}
                            />
                            {analysisResults.phoneAnalysis.riskLevel}
                          </span>
                        </div>
                      )}
                      {analysisResults.phoneAnalysis.summary && (
                        <div className="tcam-detail-block">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.phone.summary")}
                          </span>
                          <p>{analysisResults.phoneAnalysis.summary}</p>
                        </div>
                      )}
                      {analysisResults.phoneAnalysis.description && (
                        <div className="tcam-detail-block">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.phone.analysis")}
                          </span>
                          <p>{analysisResults.phoneAnalysis.description}</p>
                        </div>
                      )}
                      {analysisResults.phoneAnalysis.recommendations &&
                        analysisResults.phoneAnalysis.recommendations.length >
                          0 && (
                          <div className="tcam-detail-block">
                            <span className="tcam-detail-label">
                              {t("textConv.results.recommendations")}
                            </span>
                            <ul className="tcam-recommendations-list">
                              {analysisResults.phoneAnalysis.recommendations.map(
                                (rec, i) => (
                                  <li key={i}>{rec}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                    </div>

                    {/* Database Check Results */}
                    {analysisResults.phoneAnalysis.sources &&
                      analysisResults.phoneAnalysis.sources.length > 0 && (
                        <div className="tcam-database-section">
                          <button
                            type="button"
                            className="tcam-database-toggle"
                            onClick={() =>
                              setIsDatabaseExpanded(!isDatabaseExpanded)
                            }
                          >
                            <span>
                              {t("textAnalysis.phone.dbResults").replace(
                                "{count}",
                                analysisResults.phoneAnalysis.sources.length,
                              )}
                            </span>
                            <FontAwesomeIcon
                              icon={
                                isDatabaseExpanded ? faChevronUp : faChevronDown
                              }
                            />
                          </button>
                          {isDatabaseExpanded && (
                            <div className="tcam-database-list">
                              {analysisResults.phoneAnalysis.sources.map(
                                (source, i) => (
                                  <div key={i} className="tcam-source-card">
                                    <div className="tcam-source-header">
                                      <FontAwesomeIcon
                                        icon={faShieldAlt}
                                        className="tcam-source-icon"
                                      />
                                      <strong>{source.name}</strong>
                                    </div>
                                    <div className="tcam-source-details">
                                      <div className="tcam-detail-row">
                                        <span className="tcam-detail-label">
                                          {t("textAnalysis.phone.status")}
                                        </span>
                                        <span
                                          className={`tcam-status-chip ${
                                            source.found
                                              ? "tcam-status-found"
                                              : "tcam-status-clear"
                                          }`}
                                        >
                                          {source.found
                                            ? t("textAnalysis.phone.foundInDb")
                                            : t("textAnalysis.phone.notFound")}
                                        </span>
                                      </div>
                                      {source.reports !== null &&
                                        source.reports !== undefined && (
                                          <div className="tcam-detail-row">
                                            <span className="tcam-detail-label">
                                              {t("textAnalysis.phone.reports")}
                                            </span>
                                            <span>{source.reports}</span>
                                          </div>
                                        )}
                                      {source.category && (
                                        <div className="tcam-detail-row">
                                          <span className="tcam-detail-label">
                                            {t("textAnalysis.phone.category")}
                                          </span>
                                          <span>{source.category}</span>
                                        </div>
                                      )}
                                      {source.lastReported && (
                                        <div className="tcam-detail-row">
                                          <span className="tcam-detail-label">
                                            {t(
                                              "textAnalysis.phone.lastReported",
                                            )}
                                          </span>
                                          <span>{source.lastReported}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )}
                  </AccordionSection>
                )}

                {/* ── Conversation Pattern Analysis ── */}
                {analysisResults.textAnalysis && (
                  <AccordionSection
                    title={t("textConv.results.conversationPattern")}
                    icon={faCommentDots}
                    defaultOpen={true}
                  >
                    <div className="tcam-detail-grid">
                      {analysisResults.textAnalysis.riskLevel && (
                        <div className="tcam-detail-row">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.phone.riskLevel")}
                          </span>
                          <span
                            className={`tcam-risk-chip tcam-chip-${getRiskClass(
                              analysisResults.textAnalysis.riskLevel,
                            )}`}
                          >
                            <FontAwesomeIcon
                              icon={getRiskLevelIcon(
                                analysisResults.textAnalysis.riskLevel,
                              )}
                            />
                            {analysisResults.textAnalysis.riskLevel}
                          </span>
                        </div>
                      )}
                      {analysisResults.textAnalysis.confidence && (
                        <div className="tcam-detail-row">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.confidence")}
                          </span>
                          <span>{analysisResults.textAnalysis.confidence}</span>
                        </div>
                      )}
                      {analysisResults.textAnalysis.category && (
                        <div className="tcam-detail-row">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.category")}
                          </span>
                          <span>{analysisResults.textAnalysis.category}</span>
                        </div>
                      )}
                      {analysisResults.textAnalysis.scamType && (
                        <div className="tcam-detail-row">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.scamType")}
                          </span>
                          <span className="tcam-scam-type-chip">
                            {analysisResults.textAnalysis.scamType}
                          </span>
                        </div>
                      )}
                      {analysisResults.textAnalysis.score && (
                        <div className="tcam-detail-row">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.riskScore")}
                          </span>
                          <span>{analysisResults.textAnalysis.score}</span>
                        </div>
                      )}
                      {analysisResults.textAnalysis.description && (
                        <div className="tcam-detail-block">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.analysis")}
                          </span>
                          <p>{analysisResults.textAnalysis.description}</p>
                        </div>
                      )}
                      {analysisResults.textAnalysis.explanation && (
                        <div className="tcam-detail-block">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.explanation")}
                          </span>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: decodeAndSanitizeHtml(
                                analysisResults.textAnalysis.explanation,
                              ),
                            }}
                          />
                        </div>
                      )}
                      {analysisResults.textAnalysis.suggestions && (
                        <div className="tcam-detail-block">
                          <span className="tcam-detail-label">
                            {t("textConv.results.recommendationLabel")}
                          </span>
                          <p>{analysisResults.textAnalysis.suggestions}</p>
                        </div>
                      )}
                      {analysisResults.textAnalysis.reasoning && (
                        <div className="tcam-detail-block">
                          <span className="tcam-detail-label">
                            {t("textAnalysis.textSection.reasoning")}
                          </span>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: decodeAndSanitizeHtml(
                                analysisResults.textAnalysis.reasoning,
                              ),
                            }}
                          />
                        </div>
                      )}
                      {analysisResults.textAnalysis.riskFactors &&
                        analysisResults.textAnalysis.riskFactors.length > 0 && (
                          <div className="tcam-detail-block">
                            <span className="tcam-detail-label">
                              {t("textConv.results.detectedThreats")}
                            </span>
                            <div className="tcam-threat-chips">
                              {analysisResults.textAnalysis.riskFactors.map(
                                (threat, i) => (
                                  <span key={i} className="tcam-threat-chip">
                                    {threat}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      {analysisResults.textAnalysis.indicators &&
                        Array.isArray(
                          analysisResults.textAnalysis.indicators,
                        ) &&
                        analysisResults.textAnalysis.indicators.length > 0 && (
                          <div className="tcam-detail-block">
                            <span className="tcam-detail-label">
                              {t("textConv.results.riskIndicators")}
                            </span>
                            <div className="tcam-threat-chips">
                              {analysisResults.textAnalysis.indicators.map(
                                (ind, i) => (
                                  <span key={i} className="tcam-indicator-chip">
                                    {ind}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </AccordionSection>
                )}

                {/* ── Analysis Metadata ── */}
                {analysisResults.analysisDate && (
                  <div className="tcam-metadata">
                    {t("textAnalysis.results.completedOn")}{" "}
                    {formatDateTime(analysisResults.analysisDate)}
                  </div>
                )}

                {/* ── New Scan button ── */}
                <button
                  type="button"
                  className="btn btn-primary tcam-new-scan-btn"
                  onClick={() => {
                    handleClearAnalysis();
                    showForm();
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {t("textAnalysis.mobile.newScan")}
                </button>
              </>
            ) : (
              <div className="tcam-empty-state">
                <EmptyStatePanel
                  icon={faComments}
                  title={t("textAnalysis.mobile.noResults.title")}
                  description={t("textConv.empty.noResultsMobile")}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={showForm}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {t("textAnalysis.mobile.goToForm")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextConversationMobile;
